<template>
  <div>
    <v-dialog
      v-model="summaryModal"
      max-width="900px"
    >
      <v-card id="customer-delivery-summary-print-area">
        <v-card-actions>
          <span style="font-size: 16px; font-weight: bold;" class="rtl-pl-8">{{ $t('customerDeliverySummary.title') }}</span>
          <v-spacer></v-spacer>
          <v-btn
            v-if="$_has_perm('clear_order') && clearableOrderIds.length > 0"
            id="summary-clear-button"
            style="height: auto; background-color: #FBC02D; color: #3E2E00;"
            :disabled="actionLoading"
            @click="clearOrders"
          >
            {{ $t('customerDeliverySummary.clearButton') }}
          </v-btn>
          <v-btn
            id="summary-print-button"
            style="height: auto"
            outline
            color="primary"
            @click="printSummary"
          >
            {{ $t('printLogs.printButton') }}
          </v-btn>
          <v-btn
            id="summary-close-button"
            style="height: auto"
            outline
            @click="$emit('close')"
          >
            {{ $t('common.close') }}
          </v-btn>
        </v-card-actions>
        <v-card-text>
          <div v-if="loading" style="text-align: center; padding: 20px; color: #888;">
            {{ $t('customerDeliverySummary.loadingAllOrders') }}
          </div>
          <table
            v-else
            class="table table-bordered table-hover"
            id="myTable"
            border="1"
            cellpadding="6"
            cellspacing="0"
            style="border-collapse: collapse; width: 100%"
          >
            <thead>
              <tr :style="headerRowStyle">
                <th>{{ $t('customerDeliverySummary.totalCustomerLabel') }}</th>
                <th>{{ totalCustomers }}</th>
                <th colspan="2">{{ $t('customerDeliverySummary.totalOrdersLabel') }}</th>
                <th>{{ totalOrders }}</th>
                <th>{{ $t('columns.totalAmount') }}</th>
                <th>{{ totalAmount.toFixed(2) }} $</th>
              </tr>
              <tr :style="subHeaderRowStyle">
                <th>#</th>
                <th>{{ $t('columns.customerName') }}</th>
                <th>{{ $t('customerDeliverySummary.requestReceivedLabel') }}</th>
                <th>{{ $t('customerDeliverySummary.dateReceivedLabel') }}</th>
                <th>{{ $t('customerDeliverySummary.timeLabel') }}</th>
                <th>{{ $t('columns.phone') }}</th>
                <th>{{ $t('columns.totalAmount') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in groupedRows"
                :key="row.customer_key"
              >
                <td>{{ index + 1 }}</td>
                <td>{{ row.customer_name }}</td>
                <td>{{ row.request_received }}</td>
                <td>{{ row.date_received }}</td>
                <td>{{ row.time }}</td>
                <td>{{ row.phone }}</td>
                <td>{{ row.total_amount.toFixed(2) }}</td>
              </tr>
            </tbody>
          </table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import swal from 'sweetalert'
import moment from 'moment'

export default {
  props: ['summaryModal', 'apiResults', 'fetchUrl', 'queryParams'],

  data() {
    return {
      allResults: [],
      loading: false,
      actionLoading: false,
    }
  },

  watch: {
    summaryModal(isOpen) {
      if (isOpen) {
        this.fetchAll()
      }
    },
  },

  computed: {
    // Groups the full, unpaginated set of orders matching the page's current filters by customer
    // so staff see how many items each customer has and the combined amount to collect/verify —
    // not just what happened to be on the currently-displayed page.
    groupedRows() {
      const groups = new Map()

      ;(this.allResults || []).forEach((r) => {
        const key = r.customer_id_fk || r.user_id || r.full_name
        if (!groups.has(key)) {
          groups.set(key, {
            customer_key: key,
            customer_name: r.full_name,
            phone: r.phone_number,
            request_received: 0,
            total_amount: 0,
            received_at: null,
          })
        }

        const group = groups.get(key)
        group.request_received += 1
        group.total_amount += Number(r.total_amount) || 0

        const receivedAt = r.branch_delivery_date || r.order_date || r.created_at || r.updated_at
        if (receivedAt && (!group.received_at || moment(receivedAt).isAfter(moment(group.received_at)))) {
          group.received_at = receivedAt
        }
      })

      return Array.from(groups.values()).map((group) => ({
        ...group,
        date_received: group.received_at ? moment(group.received_at).format('YYYY-MM-DD') : '—',
        time: group.received_at ? moment(group.received_at).format('HH:mm:ss') : '—',
      }))
    },

    totalCustomers() {
      return this.groupedRows.length
    },

    totalOrders() {
      return (this.allResults || []).length
    },

    totalAmount() {
      return this.groupedRows.reduce((sum, row) => sum + row.total_amount, 0)
    },

    clearableOrderIds() {
      return (this.allResults || []).map((r) => r.order_id)
    },

    // Inline CSS text (not a class, not a style object) so the colors survive printMe, which opens
    // a blank window with only print_style.css loaded — none of the app's Bootstrap/component
    // styles carry over, only inline style attributes baked into the captured HTML. A raw string
    // (rather than a Vue style object) avoids any camelCase ambiguity on the vendor-prefixed
    // property. The print-color-adjust trio stops browsers from silently dropping background
    // colors when "print backgrounds" is off.
    headerRowStyle() {
      return 'background-color: #685AFF; color: #FFFFFF; -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact;'
    },

    subHeaderRowStyle() {
      return 'background-color: #EDE9FE; color: #3F3273; -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact;'
    },
  },

  methods: {
    // The parent's own apiResults is only the current page (per_page rows). The summary needs
    // every order matching the page's active filters, so it re-queries the same endpoint with
    // no_paginate=1 — all other filters (warehouse/status/payment/platform/search/date) are
    // carried over unchanged from the parent's queryParams. cleared_filter is force-overridden
    // to 'active' regardless of what the parent table itself is currently showing — the main
    // datatable is allowed to display already-cleared orders, but the summary (and its Clear
    // action) must never re-count/re-collect an order that's already been cleared.
    fetchAll() {
      if (!this.fetchUrl) {
        this.allResults = this.apiResults || []

        return
      }

      this.loading = true
      const token = localStorage.getItem('access_token')

      axios
        .get(this.fetchUrl, {
          params: { ...this.queryParams, cleared_filter: 'active', no_paginate: 1 },
          headers: { Authorization: 'Bearer ' + token },
        })
        .then((response) => {
          const data = response.data.data
          this.allResults = Array.isArray(data) ? data : (data.data || [])
        })
        .catch(() => {
          this.allResults = this.apiResults || []
        })
        .finally(() => {
          this.loading = false
        })
    },

    clearOrders() {
      const orderIds = this.clearableOrderIds
      if (orderIds.length === 0) return

      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('customerDeliverySummary.confirmClearOrdersText', { count: orderIds.length }),
        icon: 'warning',
        buttons: true,
      }).then((confirm) => {
        if (!confirm) return

        this.actionLoading = true
        const token = localStorage.getItem('access_token')

        axios
          .post('api/orders/mark-cleared', { orders: orderIds }, {
            headers: { Authorization: 'Bearer ' + token },
          })
          .then(() => {
            this.fetchAll()
            this.$emit('cleared')
          })
          .catch((error) => {
            const msg = (error.response && error.response.data && error.response.data.message) || this.$t('customerDeliverySummary.failedToClearOrders')
            swal(this.$t('common.error'), msg, 'error')
          })
          .finally(() => {
            this.actionLoading = false
          })
      })
    },

    printSummary() {
      $('#summary-close-button').hide()
      $('#summary-print-button').hide()
      $('#summary-clear-button').hide()

      $('#customer-delivery-summary-print-area').printMe({ path: this.$_print_stylesheet_paths() })

      $('#summary-close-button').show()
      $('#summary-print-button').show()
      $('#summary-clear-button').show()
    },
  },
}
</script>

<style>
#customer-delivery-summary-print-area {
  max-width: 900px;
}
</style>



// WEBPACK FOOTER //
// src/components/customerDeliverySummary.vue